import * as React from 'react';
import { Box, TextInput, Group, Button, Textarea, NumberInput, Select, Switch } from '@mantine/core';
import { useForm, isNotEmpty } from "@mantine/form";
import axios from 'axios';
import { useNavigate } from "react-router-dom";
import { CLIENT_ROUTE, API_ROUTE, CLIENT_TITLE } from "./constants";
import type { IResponse } from "../../types/api";
import type { ISong } from "./types";
import SearchableSelect from "../../components/common/SearchableSelect";
import { showMessage } from "../../lib/notification";
import { IMAGE_MIME_TYPE } from "@mantine/dropzone";
import FileUpload from "../../components/file-upload";
import { ISongGroup } from "../song-group/types";
import { contractItems, licenseItems, phonograms, vocalItems } from "./constants";
import { IconChevronDown } from "@tabler/icons-react";
import Page from "../../components/layout/Page";
import { useCategoryNumber } from "../../hooks";
import { assignFiles } from '../../utils/form';

interface FormValues extends Pick<ISong,
    'code1' |
    'code2' |
    'title' |
    'description' |
    'groupNumber' |
    'lyricBy' |
    'musicBy' |
    'artistName' |
    'producedBy' |
    'titleEn' |
    'singer' |
    'singerEn' |
    'vivaNumber' |
    'vocal' |
    'youtubeLink' |
    'isFeatured' |
    'featuredOrder' |
    'a100Id' |
    'vivaId' |
    'phonograms' |
    'isClip' |
    'access' |
    'contract' |
    'licenseLyric' |
    'licenseSound' |
    'licenseVideo' |
    'licenseSinger' |
    'lyrics'> {
    image: File | null;
    cover: File | null;
    categoryNumber: string;
}

interface IGroupNumberItem extends Pick<ISongGroup, '_id' | 'groupNumber' | 'groupTitle' | 'isActive' | 'isDeleted'> {
}

interface IParams extends Omit<FormValues, 'image' | 'cover' | 'categoryNumber'> {
    image?: string | null;
    cover?: string | null;
    categoryNumber: number | null;
}

const CreateArtist = () => {

    const navigate = useNavigate();
    const categories = useCategoryNumber();

    const form = useForm<FormValues>({
        initialValues: {
            code1: '',
            code2: '',
            title: '',
            description: '',
            groupNumber: 0,
            image: null,
            cover: null,
            lyricBy: '',
            musicBy: '',
            artistName: '',
            producedBy: '',
            lyrics: '',
            singerEn: '',
            singer: '',
            titleEn: '',
            vivaNumber: 0,
            vocal: 'none',
            youtubeLink: '',
            isFeatured: false,
            featuredOrder: 0,
            a100Id: 0,
            access: 0,
            contract: '',
            isClip: false,
            licenseLyric: "",
            licenseSinger: "",
            licenseSound: "",
            licenseVideo: "",
            phonograms: "",
            vivaId: 0,
            categoryNumber: ''
        },
        validate: {
            code1: isNotEmpty('Код-1 оруулна уу'),
            code2: isNotEmpty('Код-2 оруулна уу'),
            title: isNotEmpty('Гарчиг оруулна уу'),
            categoryNumber: isNotEmpty('Ангилал дугаар оруулна уу'),
        },
    });

    const groupNumberParser = ((groupNumber: IGroupNumberItem) => {
        return {
            value: groupNumber.groupNumber.toString(),
            label: `( ${groupNumber.groupNumber} ) - ${groupNumber.groupTitle}`,
        }
    })

    const handleSubmit = async (values: typeof form.values) => {
        const images = await assignFiles({
            image: values.image,
            cover: values.cover
        }, 'song')
        const params: IParams = {
            ...values,
            ...images,
            categoryNumber: values.categoryNumber ? Number(values.categoryNumber) : null
        }
        const { data } = await axios.post<IResponse<any>>(API_ROUTE, params);
        if (data.statusCode === 200) {
            navigate(CLIENT_ROUTE);
            showMessage('Амжилттай', 'green');
        } else {
            showMessage('Алдаа гарлаа');
        }
    }

    const handleError = (validationErrors: any, _values: any, _event: any) => {
        console.log('Form Error>>>', { validationErrors, _values: _values, _event: _event });
    };

    return (
        <Page>
            <Page.Header title={`${CLIENT_TITLE} үүсгэх`} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Box sx={{ width: 500 }} mx="auto">
                    <form onSubmit={form.onSubmit(handleSubmit, handleError)}>
                        <TextInput
                            withAsterisk
                            label="Код-1"
                            placeholder="Код-1 оруулна уу"
                            {...form.getInputProps('code1')}
                        />
                        <TextInput
                            withAsterisk
                            label="Код-2"
                            placeholder="Код-2 оруулна уу"
                            {...form.getInputProps('code2')}
                        />

                        <TextInput
                            withAsterisk
                            label="Дууны нэр"
                            placeholder="Дууны нэр оруулна уу"
                            {...form.getInputProps('title')}
                        />

                        <TextInput
                            label="Нэр EN"
                            placeholder="Нэр EN оруулна уу"
                            {...form.getInputProps('titleEN')}
                        />

                        <TextInput
                            label="Дуучин"
                            placeholder="Дуучин оруулна уу"
                            {...form.getInputProps('singer')}
                        />

                        <TextInput
                            label="Дуучин EN"
                            placeholder="Дуучин EN оруулна уу"
                            {...form.getInputProps('singerEN    ')}
                        />

                        <Textarea
                            autosize
                            minRows={2}
                            label="Тайлбар"
                            placeholder="Тайлбар..."
                            {...form.getInputProps('description')}
                        />

                        <FileUpload
                            value={form.values.image}
                            onChange={(value) => {
                                form.setFieldValue('image', value);
                            }}
                            label='Зураг'
                            accept={IMAGE_MIME_TYPE}
                            ratio={'1/1'}
                        />

                        <SearchableSelect<IGroupNumberItem>
                            uri={'/api/song-group/search'}
                            parser={groupNumberParser}
                            selectProps={{
                                label: 'Бүлгийн дугаар',
                                withAsterisk: true,
                                ...form.getInputProps('groupNumber')
                            }}
                        />

                        <Select
                            withAsterisk
                            label='Ангилал дугаар'
                            data={categories}
                            {...form.getInputProps('categoryNumber')}
                        />

                        <NumberInput
                            hideControls
                            label="Viva дугаар"
                            placeholder="Viva дугаар оруулна уу"
                            {...form.getInputProps('vivaNumber')}
                        />

                        <Select
                            label="Вокал"
                            rightSection={<IconChevronDown size={14} />}
                            rightSectionWidth={30}
                            styles={{ rightSection: { pointerEvents: 'none' } }}
                            data={vocalItems}
                            {...form.getInputProps('vocal')}
                        />

                        <TextInput
                            label="Үг бичсэн"
                            placeholder="Үг бичсэн оруулна уу"
                            {...form.getInputProps('lyricBy')}
                        />

                        <TextInput
                            label="Хөгжим"
                            placeholder="Хөгжим оруулна уу"
                            {...form.getInputProps('musicBy')}
                        />

                        <TextInput
                            label="Продюсер"
                            placeholder="Продюсер оруулна уу"
                            {...form.getInputProps('producedBy')}
                        />

                        <Textarea
                            autosize
                            minRows={2}
                            label="Дууны үг"
                            placeholder="Дууны үг..."
                            {...form.getInputProps('lyrics')}
                        />

                        <TextInput
                            label="YouTube холбоос"
                            placeholder="YouTube холбоос"
                            {...form.getInputProps('youtubeLink')}
                        />

                        <Switch
                            mt='xs'
                            label="Онцолсон эсэх"
                            {...form.getInputProps('isFeatured', { type: 'checkbox' })}
                        />

                        <NumberInput
                            hideControls
                            label="Онцолсон дараалал"
                            placeholder="Онцолсон дараалал"
                            {...form.getInputProps('featuredOrder')}
                        />

                        <Select
                            clearable
                            label='Фонограм'
                            data={phonograms}
                            {...form.getInputProps('phonograms')}
                        />

                        <Switch
                            mt='xs'
                            label="Клип эсэх"
                            {...form.getInputProps('isClip', { type: 'checkbox' })}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Гэрээ'
                            data={contractItems}
                            {...form.getInputProps('contract')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл үг'
                            data={licenseItems}
                            {...form.getInputProps('licenseLyric')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл ая'
                            data={licenseItems}
                            {...form.getInputProps('licenseSound')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл бичлэг'
                            data={licenseItems}
                            {...form.getInputProps('licenseVideo')}
                        />

                        <Select
                            clearable
                            withAsterisk
                            label='Зөвшөөрөл дуучин'
                            data={licenseItems}
                            {...form.getInputProps('licenseSinger')}
                        />
                    </form>
                </Box>
            </Page.Body>
            <Page.Footer>
                <Group position="right" mt="md">
                    <Button
                        type="submit"
                        color='color-secondary'
                        onClick={() => form.onSubmit(handleSubmit)()}
                    >
                        Хадгалах
                    </Button>
                </Group>
            </Page.Footer>
        </Page>
    );
};

export default CreateArtist;